import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { FileText, MapPin, Download, AlertTriangle } from "lucide-react";
import { format } from "date-fns";
import { formatCardDate, formatDateTime } from "@/design/format";
import {
  Drawer,
  DrawerHeader,
  DrawerBody,
  DrawerFooter,
  DrawerTitle,
  Button,
  Pill,
  Avatar,
  StatusRibbon,
  AssuranceBadge,
  HeimdallBadge,
  FindingCard,
  HeimdallAttribution,
  AnalystReviewingIndicator,
  PaymentMethodBadge,
  type SpendRibbonState,
} from "@/design/components";
import { useSeed, usePeopleIndex, useOrgIndex, useMutate, useActorId, useMoney, useActiveProfile, assuranceOf, spendHasGap, useFindingsForAsk, useAiReviewFor, useAnalystName } from "@/domain/hooks";
import { explainGap, addSpendComment } from "@/domain/mutations";
import { useSpendActions, useVerifySpend, useAiFindingActions } from "@/domain/useMutations";
import { useSpendAskPermissions } from "@/domain/governance";
import { requiresTransitionReason, SPEND_REASON_REQUIRED_ABOVE_UGX } from "@/domain/spendThreshold";
import { api, demoModeActive } from "@/domain/api";
import { normalizeSpendRequestDetail, evidenceAsDocument } from "@/domain/normalizeSpend";
import type { AttachedDocument, EvidenceItem, SpendRequest, SpendState } from "@/domain/types";
import { toast } from "sonner";

const RIBBON_BY_STATE: Record<SpendState, { current: SpendRibbonState; terminal?: { label: "Rejected" | "Flagged"; atIndex: number } }> = {
  proposed: { current: "Proposed" },
  approved_for_spend: { current: "Approved for spend" },
  committed: { current: "Committed" },
  evidence_submitted: { current: "Evidence submitted" },
  confirmed: { current: "Confirmed" },
  rejected: { current: "Proposed", terminal: { label: "Rejected", atIndex: 1 } },
  flagged: { current: "Proposed", terminal: { label: "Flagged", atIndex: 1 } },
};

// A single attachment row with an inline preview. Used for both the ask's own
// supporting documents and the evidence a reporter submits against it, so the two
// look and behave identically (§6). Images/PDFs with a durable url expand in place;
// metadata-only seed documents stay a static chip.
function DocumentRow({ doc, open, onToggle }: { doc: AttachedDocument; open: boolean; onToggle: () => void }) {
  const size = doc.sizeBytes > 0 ? `${Math.round(doc.sizeBytes / 1024)} KB` : null;
  const previewable = !!doc.url && (doc.kind === "image" || doc.kind === "pdf");
  const inner = (
    <>
      {doc.url && doc.kind === "image" ? (
        <img src={doc.url} alt={doc.filename} className="h-9 w-9 shrink-0 rounded object-cover" />
      ) : (
        <FileText className="h-4 w-4 shrink-0 text-faint" />
      )}
      <span className="flex-1 truncate text-ink">{doc.filename}</span>
      {previewable && <span className="text-xs text-brand">{open ? "Hide" : "Preview"}</span>}
      {size && <span className="text-xs text-faint">{size}</span>}
    </>
  );
  return (
    <li>
      {previewable ? (
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={open}
          className="focus-ring flex w-full items-center gap-2 rounded-md border border-line px-3 py-2 text-left text-sm transition-colors hover:border-brand hover:bg-subtle"
        >
          {inner}
        </button>
      ) : (
        <div className="flex items-center gap-2 rounded-md border border-line px-3 py-2 text-sm">
          {inner}
        </div>
      )}
      {previewable && open && (
        <div className="mt-1.5 overflow-hidden rounded-md border border-line bg-subtle">
          {doc.kind === "image" ? (
            <img src={doc.url} alt={doc.filename} className="max-h-[28rem] w-full object-contain" />
          ) : (
            <iframe src={doc.url} title={doc.filename} className="h-[28rem] w-full bg-card" />
          )}
          <div className="flex justify-end border-t border-line bg-card px-2 py-1.5">
            <a
              href={doc.url}
              download={doc.filename}
              className="focus-ring inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium text-brand hover:bg-subtle"
            >
              <Download className="h-3.5 w-3.5" /> Download
            </a>
          </div>
        </div>
      )}
    </li>
  );
}

// One evidence package: the shared attachment row (preview + download, exactly
// like the ask's own documents) plus the evidence-only metadata around it —
// who captured it, when, where, and the note they wrote.
function EvidenceRow({
  item,
  submitterName,
  open,
  onToggle,
}: {
  item: EvidenceItem;
  submitterName: string;
  open: boolean;
  onToggle: () => void;
}) {
  return (
    <li className="rounded-md border border-line p-3">
      <ul>
        <DocumentRow doc={evidenceAsDocument(item)} open={open} onToggle={onToggle} />
      </ul>
      {item.note && <p className="mt-2 text-sm text-mute">{item.note}</p>}
      <div className="mt-1.5 flex flex-wrap items-center gap-3 text-xs text-faint">
        <span>{submitterName}</span>
        <span>{formatDateTime(item.capturedAt)}</span>
        {item.geo && (
          <span className="flex items-center gap-1">
            <MapPin className="h-3 w-3" />
            {item.geo.lat.toFixed(3)}, {item.geo.lng.toFixed(3)}
          </span>
        )}
      </div>
    </li>
  );
}

export function SpendAskDrawer({
  ask,
  open,
  onOpenChange,
  canApprove = true,
  onActionComplete,
}: {
  ask: SpendRequest | null;
  open: boolean;
  onOpenChange: (o: boolean) => void;
  canApprove?: boolean;
  /** Called after a successful approve/query/reject/confirm - lets a caller
   * backed by a real (non-demo) fetch, rather than the live seed store,
   * refetch so an actioned ask actually leaves its queue. */
  onActionComplete?: () => void;
}) {
  const seed = useSeed();
  const people = usePeopleIndex();
  const orgs = useOrgIndex();
  const mutate = useMutate();
  const actorId = useActorId();
  const { approve, reject, query, confirm, isPending: actionPending } = useSpendActions();
  const { verify, isPending: verifying } = useVerifySpend();
  const { accept: acceptFindingHook, dismiss: dismissFindingHook } = useAiFindingActions();
  const money = useMoney();
  const profile = useActiveProfile();
  const analystName = useAnalystName();
  const reviewFor = useAiReviewFor();
  const findings = useFindingsForAsk(ask?.id ?? "");
  const [mode, setMode] = useState<null | "query" | "reject" | "approve" | "confirm">(null);
  const [comment, setComment] = useState("");
  const [gapText, setGapText] = useState("");
  const [threadText, setThreadText] = useState("");
  const [dismissingId, setDismissingId] = useState<string | null>(null);
  const [dismissReason, setDismissReason] = useState("");
  const [attachedFindingIds, setAttachedFindingIds] = useState<string[]>([]);
  const [openCitations, setOpenCitations] = useState<string | null>(null);
  const [openDocId, setOpenDocId] = useState<string | null>(null);
  const permissionsFor = useSpendAskPermissions();

  // Live (API-first) mode must resolve the ask's evidence, line items and
  // supporting documents from the backend. Everything below used to read them
  // out of the seed store, which off demo holds no record of what a Reporter
  // actually submitted - so the Assurance panel was pinned at "self-reported"
  // and told oversight the figure was unevidenced no matter what had been
  // uploaded. Same query key as the Reporter's evidence screen, so the two
  // share one cache entry rather than each fetching the ask separately.
  const demo = demoModeActive();
  const askId = ask?.id ?? "";
  const { data: detail } = useQuery({
    queryKey: ["spend-request-detail", askId],
    queryFn: async () => {
      const d = await api.getSpendRequestDetail(askId);
      if (!d.spendRequest) return null;
      return normalizeSpendRequestDetail({ ...d, spendRequest: d.spendRequest });
    },
    enabled: !demo && open && !!askId,
  });

  if (!ask) return null;
  // Re-read the ask live from the store so store mutations reflect immediately.
  // The `ask` prop is a snapshot the parent holds in local state (`useState`),
  // so it does not update in place — without this, a newly sent discussion
  // comment only appears after the drawer is closed and reopened.
  // Off demo the seed holds no row for a real ask, so the fetched detail is the
  // live copy - it carries the verification fields and supporting documents the
  // queue's list-row normaliser drops. Both fall back to the prop.
  ask = demo
    ? seed.spendRequests.find((r) => r.id === ask!.id) ?? ask
    : detail?.ask ?? ask;
  // Governance (§6 separation of duties): approving a proposed ask is the
  // project lead's call (prime, or sub lead within their sub-project); confirming
  // submitted evidence is oversight's. The caller's `canApprove` prop still
  // read-onlys the drawer wholesale (e.g. assurance views).
  const { mayApprove, mayConfirm, awaitingVerification } = permissionsFor(ask);
  const showApproveFooter = canApprove && mayApprove;
  const showConfirmFooter = canApprove && mayConfirm;
  // Above-threshold ask not yet verified by Stratus, viewed by the contractor lead:
  // explain why there is no approve action yet (Stratus must verify it first). Stratus
  // itself is excluded — it sees the "Mark independently verified" action instead.
  const showAwaitingStratus = canApprove && !mayApprove && awaitingVerification && profile.group !== "oversight" && (ask.state === "proposed" || ask.state === "flagged") && ask.reporterId !== actorId;
  // Stratus oversight can send a proposed ask back to the reporter with a query, separate
  // from the approve/verify gates (a query is not an approval). The contractor's own Query
  // lives in the approve footer below; this covers Stratus, who has no approve footer.
  const showOversightQuery = canApprove && profile.group === "oversight" && !showApproveFooter && (ask.state === "proposed" || ask.state === "flagged") && ask.reporterId !== actorId;
  const reporter = people[ask.reporterId];
  const org = orgs[ask.organisationId];
  const project = seed.projects.find((p) => p.id === ask.projectId);
  const sub = seed.subProjects.find((sp) => sp.id === ask.subProjectId);
  const milestone = seed.milestones.find((m) => m.id === ask.milestoneId);
  const items = demo ? seed.spendItems.filter((i) => i.spendRequestId === ask.id) : detail?.items ?? [];
  const evidence = demo
    ? (seed.evidence ?? []).filter((e) => e.linkedAskId === ask!.id)
    : detail?.evidence ?? [];
  // Evidence submitted against the ask itself rather than one of its line items
  // (§6.5's bundle submission). Anything tied to an item is rendered under that
  // item instead; the itemIds check keeps evidence visible if its item is not in
  // the list rather than silently dropping it.
  const itemIds = new Set(items.map((i) => i.id));
  const askLevelEvidence = evidence.filter((e) => !e.linkedItemId || !itemIds.has(e.linkedItemId));
  const ribbon = RIBBON_BY_STATE[ask.state];

  const isPending = ask.state === "proposed" || ask.state === "flagged";
  const isAwaitingConfirmation = ask.state === "evidence_submitted";
  // Over the pack's reason threshold, approve/confirm must carry a written
  // justification exactly as reject already does - so those buttons open the
  // comment box first rather than firing straight at the API and 500ing.
  const needsReason = requiresTransitionReason(ask);

  // Assurance (v3 §Chunk 2) — derived from this ask's own evidence and items in
  // both modes, rather than from whatever the seed store happens to hold.
  const level = assuranceOf(ask, evidence, items);
  const gap = spendHasGap(ask, level);
  const canVerify = profile.group === "oversight";
  const canExplain = profile.group === "contractor" || profile.group === "oversight";
  const provenance =
    level === "verified"
      ? `Independently verified by ${people[ask.verifiedById ?? ""]?.fullName ?? "oversight"}${ask.verifiedAt ? ` on ${formatCardDate(ask.verifiedAt)}` : ""}.${ask.verificationNote ? ` ${ask.verificationNote}` : ""}`
      : level === "statement_matched"
      ? `Reconciled against a bank / mobile-money statement (${ask.statementMatchRef}).`
      : level === "document_backed"
      ? "Backed by receipts, invoices or site photos attached to the spend."
      : "Self-reported only: no document, statement match or independent check yet.";

  // Heimdall review (advisory; never gates the lifecycle)
  const review = reviewFor(ask);
  const openFindings = findings.filter((f) => f.status === "open");

  async function doAccept(id: string) {
    await acceptFindingHook(id);
    toast.success("Finding accepted");
  }
  async function doDismiss(id: string) {
    if (!dismissReason.trim()) return;
    await dismissFindingHook(id, dismissReason.trim());
    setDismissingId(null);
    setDismissReason("");
    toast.message("Finding dismissed");
  }

  async function doVerify() {
    await verify(ask!.id);
    toast.success(`Verified '${ask!.title}'`);
    // Verifying is what actually moves a threshold ask out of Stratus's
    // queue (thresholdAsks filters on !verifiedAt) - same "leaves the queue"
    // case act() handles above.
    onActionComplete?.();
  }
  function doExplain() {
    if (!gapText.trim()) return;
    mutate((s) => explainGap(s, ask!.id, actorId, gapText.trim()));
    toast.success("Explanation recorded against the figure");
    setGapText("");
  }
  function doComment() {
    if (!threadText.trim()) return;
    mutate((s) => addSpendComment(s, ask!.id, actorId, threadText.trim()));
    setThreadText("");
  }

  /** Pull the server's own message off a failed action, so a compliance-pack
   * refusal reads as itself instead of a dead button. Both router error shapes
   * (structuredRouterError and the 403/400 branch) put it on `error`. */
  function actionErrorMessage(err: unknown, fallback: string): string {
    const body = (err as { response?: { data?: { error?: string } } })?.response?.data;
    return body?.error || fallback;
  }

  async function act(kind: "approve" | "query" | "reject" | "confirm") {
    // Above the pack's reason threshold every transition must carry a written
    // justification (see requiresTransitionReason). The footers below route
    // approve/confirm through the comment box in that case, so `comment` is
    // populated; below the threshold it stays empty and `reason` goes undefined.
    const reason = needsReason ? comment.trim() : "";
    if (needsReason && !reason && (kind === "approve" || kind === "confirm")) return;
    try {
      if (kind === "confirm") {
        await confirm(ask!.id, reason || undefined);
        toast.success(`Spend confirmed · '${ask!.title}'`);
        reset();
        onActionComplete?.();
        return;
      }
      if (kind === "approve") {
        await approve(ask!.id, reason || undefined);
        toast.success(`Approved '${ask!.title}'`);
        reset();
        onActionComplete?.();
      } else if (kind === "query") {
        if (!comment.trim()) return;
        const attached = findings.filter((f) => attachedFindingIds.includes(f.id));
        const body = attached.length
          ? `${comment.trim()}\n\nFlagged by ${analystName}:\n${attached.map((f) => `• ${f.statement}`).join("\n")}`
          : comment.trim();
        await query(ask!.id, body);
        toast.message(`Query sent to ${reporter?.fullName ?? "reporter"}${attached.length ? ` with ${attached.length} finding${attached.length === 1 ? "" : "s"} attached` : ""}`);
        reset();
        onActionComplete?.();
      } else {
        if (!comment.trim()) return;
        await reject(ask!.id, comment.trim());
        toast.message(`Rejected '${ask!.title}'`);
        reset();
        onActionComplete?.();
      }
    } catch (err) {
      // Without this the promise rejected unhandled: no toast, drawer frozen
      // open, button apparently inert. That is how the missing-reason 500 above
      // stayed invisible. Drawer deliberately stays open so the text is not lost.
      toast.error(actionErrorMessage(err, `Couldn't ${kind} '${ask!.title}' — please retry`));
    }
  }
  function reset() {
    setMode(null);
    setComment("");
    setAttachedFindingIds([]);
    onOpenChange(false);
  }

  return (
    <Drawer open={open} onOpenChange={onOpenChange} width="640px">
      <DrawerHeader>
        <DrawerTitle className="text-base font-semibold text-ink">{ask.title}</DrawerTitle>
        <p className="mt-1 flex items-center gap-2 text-sm text-mute">
          <span className="text-lg font-semibold tabular-nums text-ink">{money(ask.amount)}</span>
          {ask.path === "recorded" && <Pill variant="neutral">Recorded · self-authorised</Pill>}
          {ask.path === "pre_auth" && <Pill variant="info">Pre-authorised</Pill>}
          {ask.thresholdFlag && <Pill variant="warn">Threshold</Pill>}
          <PaymentMethodBadge method={ask.paymentMethod} />
          {/* TODO(heimdall): AI analyst not built yet — re-enable the review badge when Heimdall ships.
          {review.state === "pending"
            ? <AnalystReviewingIndicator name={analystName} />
            : <HeimdallBadge state={review.state} count={openFindings.length} name={analystName} />}
          */}
        </p>
        <p className="mt-1 text-xs text-faint">
          {reporter?.fullName} · {reporter?.role} · {org?.name}
        </p>
      </DrawerHeader>

      <DrawerBody className="space-y-6">
        <StatusRibbon current={ribbon.current} terminal={ribbon.terminal} />

        {/* TODO(heimdall): AI analyst not built yet — re-enable the whole "{analyst} review"
            section (findings list, accept/dismiss, cited evidence) when Heimdall ships.
        <Section label={`${analystName} review`}>
          {findings.length === 0 ? (
            <div className="flex items-center justify-between gap-2 rounded-md border border-line bg-card px-3 py-2">
              <HeimdallAttribution name={analystName} firstMention />
              {review.state === "pending"
                ? <AnalystReviewingIndicator name={analystName} />
                : <span className="text-xs text-ok">Checks passed</span>}
            </div>
          ) : (
            <div className="space-y-2">
              {findings.map((f) => {
                const citedAsks = (f.citations.askIds ?? []).map((id) => seed.spendRequests.find((r) => r.id === id)).filter(Boolean) as SpendRequest[];
                const citedEvidence = (f.citations.evidenceIds ?? []).map((id) => seed.evidence?.find((e) => e.id === id)).filter(Boolean);
                const citedCount = citedAsks.length + citedEvidence.length + (f.citations.refs?.length ?? 0);
                const canDecide = (showApproveFooter || showConfirmFooter) && f.status === "open";
                return (
                  <div key={f.id}>
                    <FindingCard
                      finding={f}
                      name={analystName}
                      decidedByName={f.decidedById ? people[f.decidedById]?.fullName : undefined}
                      citationsLabel={citedCount ? `${openCitations === f.id ? "Hide" : "View"} cited evidence (${citedCount})` : undefined}
                      onOpenCitations={citedCount ? () => setOpenCitations(openCitations === f.id ? null : f.id) : undefined}
                      onAccept={canDecide ? () => doAccept(f.id) : undefined}
                      onDismiss={canDecide ? () => setDismissingId(dismissingId === f.id ? null : f.id) : undefined}
                    />
                    {openCitations === f.id && citedCount > 0 && (
                      <div className="mt-1.5 grid grid-cols-2 gap-2">
                        {citedAsks.map((r) => (
                          <div key={r.id} className="rounded-md border border-line bg-subtle p-2 text-xs">
                            <p className="font-medium text-ink">{r.title}</p>
                            <p className="mt-0.5 tabular-nums text-mute">{money(r.amount)}</p>
                            {r.supplier && <p className="text-faint">{r.supplier}</p>}
                          </div>
                        ))}
                        {citedEvidence.map((e) => (
                          <div key={e!.id} className="overflow-hidden rounded-md border border-line bg-subtle text-xs">
                            <img src={e!.url} alt={e!.filename ?? "Evidence"} className="h-20 w-full object-cover" />
                            <p className="truncate px-2 py-1 text-faint">{e!.filename ?? e!.note}</p>
                          </div>
                        ))}
                        {(f.citations.refs ?? []).map((ref) => (
                          <div key={ref} className="rounded-md border border-line bg-subtle p-2 text-xs text-mute">{ref}</div>
                        ))}
                      </div>
                    )}
                    {dismissingId === f.id && (
                      <div className="mt-1.5 flex gap-2">
                        <input
                          autoFocus
                          value={dismissReason}
                          onChange={(e) => setDismissReason(e.target.value)}
                          placeholder="Why dismiss this finding? (required)"
                          className="min-w-0 flex-1 rounded-md border border-line px-2 py-1 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-brand"
                        />
                        <Button size="sm" onClick={() => doDismiss(f.id)} disabled={!dismissReason.trim()}>Dismiss</Button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </Section>
        */}

        <Section label="Context">
          <div className="flex flex-wrap items-center gap-1.5 text-sm text-mute">
            {project && <Pill variant="outline">{project.name}</Pill>}
            {sub && <Pill variant="outline">{sub.name}</Pill>}
            {milestone && <Pill variant="outline">{milestone.name}</Pill>}
          </div>
          <p className="mt-2 text-xs text-faint">Compliance pack: {seed.workspace.compliancePackName}</p>
          {ask.thresholdFlag && (
            <p className="mt-2 rounded-md bg-warnSoft px-3 py-2 text-xs text-warn">
              {ask.thresholdFlag.rule}. Routed to {ask.thresholdFlag.routedTo}.
            </p>
          )}
          {ask.paymentMethod === "cash" && (
            <div className="mt-2 rounded-md bg-warnSoft px-3 py-2 text-xs text-warn">
              <p className="font-medium">Cash spend: stricter evidence required.</p>
              <p className="mt-0.5">A stated reason and proof of the resources bought are needed, not only proof of payment.</p>
              {ask.cashReason && <p className="mt-1 text-mute">Reason: {ask.cashReason}</p>}
            </div>
          )}
        </Section>

        <Section label="Purpose">
          <p className="text-sm leading-relaxed text-mute">{ask.purpose}</p>
          {ask.supplier && <p className="mt-2 text-sm text-ink">Supplier: {ask.supplier}</p>}
        </Section>

        <Section label="Assurance">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <AssuranceBadge level={level} />
            {canVerify && level !== "verified" && (
              <Button size="sm" variant="secondary" onClick={doVerify}>Mark independently verified</Button>
            )}
          </div>
          <p className="mt-2 text-xs text-mute">{provenance}</p>
          {gap && (
            <div className="mt-3 rounded-md border border-warn/30 bg-warnSoft px-3 py-2.5">
              {ask.gapExplanation ? (
                <div className="text-xs">
                  <p className="font-medium text-warn">Gap explained</p>
                  <p className="mt-0.5 text-mute">{ask.gapExplanation.body}</p>
                  <p className="mt-0.5 text-faint">{people[ask.gapExplanation.authorId]?.fullName ?? ask.gapExplanation.authorId}</p>
                </div>
              ) : (
                <div>
                  <p className="text-xs font-medium text-warn">Money is out but the figure is only self-reported. Attach evidence, reconcile it, or explain the gap.</p>
                  {canExplain && (
                    <div className="mt-2 flex gap-2">
                      <input
                        value={gapText}
                        onChange={(e) => setGapText(e.target.value)}
                        placeholder="e.g. cash purchase at a rural supplier; no formal receipt issued"
                        className="min-w-0 flex-1 rounded-md border border-line px-2 py-1 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-brand"
                      />
                      <Button size="sm" onClick={doExplain} disabled={!gapText.trim()}>Save</Button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </Section>

        {ask.documents.length > 0 && (
          <Section label="Supporting documents">
            <ul className="flex flex-col gap-2">
              {ask.documents.map((d) => (
                <DocumentRow
                  key={d.id}
                  doc={d}
                  open={openDocId === d.id}
                  onToggle={() => setOpenDocId(openDocId === d.id ? null : d.id)}
                />
              ))}
            </ul>
          </Section>
        )}

        {askLevelEvidence.length > 0 && (
          <Section label={`Evidence (${askLevelEvidence.length})`}>
            <ul className="flex flex-col gap-2">
              {askLevelEvidence.map((e) => (
                <EvidenceRow
                  key={e.id}
                  item={e}
                  submitterName={people[e.submittedById]?.fullName ?? e.submittedById}
                  open={openDocId === e.id}
                  onToggle={() => setOpenDocId(openDocId === e.id ? null : e.id)}
                />
              ))}
            </ul>
          </Section>
        )}

        {items.length > 0 && (
          <Section label={`Spend breakdown (${items.length} item${items.length === 1 ? "" : "s"})`}>
            {(() => {
              const logged = items.reduce((s, i) => s + i.amount, 0);
              const remaining = ask.amount - logged;
              const overBudget = remaining < 0;
              return (
                <div className="mb-3 rounded-md border border-line p-3">
                  {/* Reporters can now record over the approved budget; the overspend is flagged
                      here for oversight to weigh before confirming the spend (§6 / §10). */}
                  {overBudget && (
                    <div className="mb-3 flex items-start gap-2 rounded-md border border-warn bg-warnSoft px-3 py-2 text-xs text-warnStrong">
                      <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                      <span>Recorded spend exceeds the approved budget of {money(ask.amount)} by <span className="font-semibold tabular-nums">{money(-remaining)}</span>.</span>
                    </div>
                  )}
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-faint">Approved budget</span>
                    <span className="font-medium tabular-nums text-ink">{money(ask.amount)}</span>
                  </div>
                  <div className="mt-2 h-2 overflow-hidden rounded-full bg-subtle">
                    <div className={`h-full ${overBudget ? "bg-bad" : "bg-brand"}`} style={{ width: `${Math.min(100, (logged / ask.amount) * 100)}%` }} />
                  </div>
                  <div className="mt-1.5 flex items-center justify-between text-xs text-faint">
                    <span>Itemised {money(logged)}</span>
                    <span className={overBudget ? "text-bad" : ""}>{overBudget ? `Over by ${money(-remaining)}` : `Remaining ${money(remaining)}`}</span>
                  </div>
                </div>
              );
            })()}
            <ul className="flex flex-col gap-2">
              {items.map((i) => (
                <li key={i.id} className="rounded-md border border-line p-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-ink">{i.description}</span>
                    <span className="text-sm tabular-nums text-ink">{money(i.amount)}</span>
                  </div>
                  <div className="mt-1 flex items-center gap-3 text-xs text-faint">
                    {i.supplier && <span>{i.supplier}</span>}
                    {i.geoTag && (
                      <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{i.geoTag.lat.toFixed(3)}, {i.geoTag.lng.toFixed(3)}</span>
                    )}
                  </div>
                  {i.evidence.length > 0 && (
                    <ul className="mt-2 flex flex-col gap-2">
                      {i.evidence.map((d) => (
                        <DocumentRow
                          key={d.id}
                          doc={d}
                          open={openDocId === d.id}
                          onToggle={() => setOpenDocId(openDocId === d.id ? null : d.id)}
                        />
                      ))}
                    </ul>
                  )}
                </li>
              ))}
            </ul>
          </Section>
        )}

        {(ask.reviewerComment || ask.rejectionReason) && (
          <Section label="Latest comment">
            <p className="rounded-md bg-subtle px-3 py-2 text-sm text-mute">
              {ask.rejectionReason ?? ask.reviewerComment}
            </p>
          </Section>
        )}

        <Section label="Discussion">
          {(ask.thread ?? []).length === 0 ? (
            <p className="text-sm text-faint">No discussion yet.</p>
          ) : (
            <ul className="space-y-2">
              {(ask.thread ?? []).map((c) => (
                <li key={c.id} className="flex items-start gap-2">
                  <Avatar initials={people[c.authorId]?.avatarInitials ?? "?"} size={26} />
                  <div className="min-w-0 flex-1 rounded-md border border-line bg-card px-3 py-2">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-xs font-medium text-ink">{people[c.authorId]?.fullName ?? c.authorId}</span>
                      <span className="text-[11px] text-faint">{c.kind === "query" ? "Query" : "Comment"} · {format(new Date(c.at), "d MMM")}</span>
                    </div>
                    <p className="mt-0.5 text-sm text-mute">{c.body}</p>
                  </div>
                </li>
              ))}
            </ul>
          )}
          <div className="mt-2 flex gap-2">
            <input
              value={threadText}
              onChange={(e) => setThreadText(e.target.value)}
              placeholder="Add a comment or reply"
              className="min-w-0 flex-1 rounded-md border border-line px-2 py-1.5 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-brand"
            />
            <Button size="sm" onClick={doComment} disabled={!threadText.trim()}>Send</Button>
          </div>
        </Section>

        <Section label="Submitted">
          <p className="text-sm text-mute">{formatDateTime(ask.submittedAt)}</p>
        </Section>
      </DrawerBody>

      {showConfirmFooter && isAwaitingConfirmation && (
        <DrawerFooter>
          <div className="flex items-center justify-end gap-2">
            <Button variant="ghost" className="text-bad hover:bg-badSoft" disabled={actionPending} onClick={() => setMode("reject")}>Reject</Button>
            {/* Above the pack threshold, confirming opens the reason box instead
                of firing immediately - the server rejects a reasonless transition. */}
            <Button disabled={actionPending} onClick={() => (needsReason ? setMode("confirm") : act("confirm"))}>
              Confirm spend · {money(ask.amount)}
            </Button>
          </div>
          {(mode === "reject" || mode === "confirm") && (
            <div className="mt-2 space-y-2">
              <textarea
                autoFocus
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder={
                  mode === "reject"
                    ? "Reason for rejection (required)"
                    : `Reason for confirming this spend — required over ${money(SPEND_REASON_REQUIRED_ABOVE_UGX)} and recorded in the audit trail`
                }
                className="h-20 w-full rounded-md border border-line p-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand"
              />
              <div className="flex justify-end gap-2">
                <Button variant="ghost" onClick={() => { setMode(null); setComment(""); }}>Cancel</Button>
                {mode === "reject" ? (
                  <Button variant="danger" onClick={() => act("reject")} disabled={!comment.trim()}>Reject ask</Button>
                ) : (
                  <Button onClick={() => act("confirm")} disabled={actionPending || !comment.trim()}>Confirm spend</Button>
                )}
              </div>
            </div>
          )}
        </DrawerFooter>
      )}

      {showApproveFooter && isPending && (
        <DrawerFooter className="flex-col items-stretch gap-2">
          {mode ? (
            <div className="space-y-2">
              <textarea
                autoFocus
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder={
                  mode === "reject"
                    ? "Reason for rejection (required)"
                    : mode === "approve"
                    ? `Reason for approving this spend — required over ${money(SPEND_REASON_REQUIRED_ABOVE_UGX)} and recorded in the audit trail`
                    : "Comment for the reporter (required)"
                }
                className="h-20 w-full rounded-md border border-line p-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand"
              />
              {mode === "query" && openFindings.length > 0 && (
                <div className="rounded-md border border-line p-2">
                  <p className="mb-1.5 text-[11px] font-medium text-faint">Attach {analystName} findings so the reporter sees what to fix</p>
                  <ul className="space-y-1">
                    {openFindings.map((f) => (
                      <li key={f.id} className="flex items-start gap-2 text-xs text-mute">
                        <input
                          type="checkbox"
                          id={`attach-${f.id}`}
                          checked={attachedFindingIds.includes(f.id)}
                          onChange={(e) =>
                            setAttachedFindingIds((prev) =>
                              e.target.checked ? [...prev, f.id] : prev.filter((x) => x !== f.id),
                            )
                          }
                          className="mt-0.5"
                        />
                        <label htmlFor={`attach-${f.id}`} className="flex-1 cursor-pointer">{f.statement}</label>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <div className="flex justify-end gap-2">
                <Button variant="ghost" onClick={() => { setMode(null); setComment(""); }}>Cancel</Button>
                <Button
                  variant={mode === "reject" ? "danger" : "primary"}
                  onClick={() => act(mode)}
                  disabled={actionPending || !comment.trim()}
                >
                  {mode === "reject" ? "Reject ask" : mode === "approve" ? `Approve ${money(ask.amount)}` : "Send query"}
                </Button>
              </div>
            </div>
          ) : ask.reporterId === actorId ? (
            <p className="rounded-md bg-warnSoft px-3 py-2 text-xs text-warn">
              You raised this ask, so you cannot approve it. Segregation of duties means a different authoriser must review it.
            </p>
          ) : (
            <div className="flex items-center justify-end gap-2">
              <Button variant="ghost" className="text-bad hover:bg-badSoft" disabled={actionPending} onClick={() => setMode("reject")}>Reject</Button>
              <Button variant="secondary" disabled={actionPending} onClick={() => setMode("query")}>Query</Button>
              {/* Above the pack threshold this opens the reason box rather than
                  approving directly - a reasonless transition is refused server-side. */}
              <Button disabled={actionPending} onClick={() => (needsReason ? setMode("approve") : act("approve"))}>
                Approve {money(ask.amount)}
              </Button>
            </div>
          )}
        </DrawerFooter>
      )}

      {showAwaitingStratus && (
        <DrawerFooter>
          <p className="rounded-md bg-warnSoft px-3 py-2 text-xs text-warn">
            This ask is above the compliance threshold, so Stratus must mark it independently verified before you can approve it. It will appear in your queue once Stratus has verified it.
          </p>
        </DrawerFooter>
      )}

      {showOversightQuery && (
        <DrawerFooter className="flex-col items-stretch gap-2">
          {mode === "query" ? (
            <div className="space-y-2">
              <textarea
                autoFocus
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Comment for the reporter (required)"
                className="h-20 w-full rounded-md border border-line p-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand"
              />
              <div className="flex justify-end gap-2">
                <Button variant="ghost" onClick={() => { setMode(null); setComment(""); }}>Cancel</Button>
                <Button onClick={() => act("query")} disabled={!comment.trim()}>Send query</Button>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-end gap-2">
              <Button variant="secondary" disabled={actionPending} onClick={() => setMode("query")}>Query reporter</Button>
            </div>
          )}
        </DrawerFooter>
      )}
    </Drawer>
  );
}

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <section>
      <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-faint">{label}</h4>
      {children}
    </section>
  );
}
